<script lang="ts" setup>
interface Props {
  unit?: string;
  pos?: 'left' | 'right' | 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
}

const props = withDefaults(defineProps<Props>(), {
  unit: '',
  pos: 'left',
  align: 'center',
});
</script>

<template>
  <div
    :class="[
      'unit-container',
      {
        'row-direction': props.pos === 'left' || props.pos === 'right',
        'column-direction': props.pos === 'top' || props.pos === 'bottom',
        'only-content': !props.unit,
      },
    ]"
  >
    <!-- left & top -->
    <div
      v-if="unit && (props.pos === 'left' || props.pos === 'top')"
      :class="['unit-container__unit-wrapper', `${props.align}`, `${props.pos}`]"
    >
      <span
        :title="props.unit"
        class="unit-area"
      >
        {{ props.unit }}
      </span>
    </div>

    <!-- content -->
    <div class="unit-container__content">
      <slot />
    </div>

    <!-- right & bottom -->
    <div
      v-if="unit && (props.pos === 'right' || props.pos === 'bottom')"
      :class="[
        'unit-container__unit-wrapper',
        `${props.align}`,
        `${props.pos}`,
        {
          'row-direction': props.pos === 'right',
          'column-direction': props.pos === 'bottom',
        },
      ]"
    >
      <span
        :title="props.unit"
        class="unit-area"
      >
        {{ props.unit }}
      </span>
    </div>
  </div>
</template>

<style lang="scss">
.unit-container {
  --line-chart-widget-unit-area-size: 15px;

  display: flex;
  width: 100%;
  height: 100%;

  &__unit-wrapper {
    display: flex;
    width: var(--line-chart-widget-unit-area-size);
    height: 100%;
    overflow: visible;
    user-select: none;

    .unit-area {
      font-size: 10px;
      color: var(--chart-unit-color);
      white-space: nowrap;

      @include shortening(inline-block, auto);
    }
  }

  &.row-direction .unit-container__unit-wrapper {
    justify-content: center;
    width: var(--line-chart-widget-unit-area-size);
    height: 100%;

    .unit-area {
      writing-mode: vertical-rl;
      text-orientation: sideways;
      max-height: 100%;
      overflow: hidden;
    }

    &.left {
      .unit-area {
        writing-mode: vertical-lr;
        transform: rotate(180deg);
      }

      &.start {
        align-items: flex-end;
      }
      &.center {
        align-items: center;
      }
      &.end {
        align-items: flex-start;
      }
    }

    &.right {
      &.start {
        align-items: flex-start;
      }
      &.center {
        align-items: center;
      }
      &.end {
        align-items: flex-end;
      }
    }
  }
  &.row-direction .unit-container__content {
    width: calc(100% - var(--line-chart-widget-unit-area-size));
    height: 100%;
  }

  &.column-direction {
    flex-direction: column;
  }
  &.column-direction .unit-container__unit-wrapper {
    align-items: center;
    width: 100%;
    height: var(--line-chart-widget-unit-area-size);

    &.start {
      justify-content: flex-start;
    }
    &.center {
      justify-content: center;
    }
    &.end {
      justify-content: flex-end;
    }
  }
  &.column-direction .unit-container__content {
    width: 100%;
    height: calc(100% - var(--line-chart-widget-unit-area-size));
  }

  &.only-content .unit-container__content {
    width: 100%;
    height: 100%;
  }
}
</style>
